/**
 * Landlord API - Handles all landlord-related API calls
 */
import { fetchWithAuth } from '../lib/api';
import { getStorageItem } from '../utils/storage';
import { getApiBaseUrl } from '../config/constants';

/**
 * Register a new landlord
 * @param {Object} landlordData - Landlord registration data
 * @param {string} landlordData.first_name - First name (required)
 * @param {string} landlordData.last_name - Last name (required)
 * @param {string} landlordData.email - Landlord email (required)
 * @param {string} [landlordData.password] - Landlord password (optional for waitlist signup, min 8 characters if provided)
 * @returns {Promise<{data: Object|null, error: Object|null}>} Created landlord data or error
 */
export const registerLandlord = async (landlordData: Record<string, unknown>) => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/landlords/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(landlordData),
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        const message = errorData.detail?.message || errorData.detail || 'Failed to create account. Please try again.';
        return {
          data: null,
          error: { message }
        };
      } catch (e) {
        return {
          data: null,
          error: {
            message: 'Failed to create account. Please try again.'
          }
        };
      }
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    const e = error as Error;
    return {
      data: null,
      error: {
        message: e.message || 'Failed to create account. Please try again.'
      }
    };
  }
};

/**
 * Login landlord
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - Landlord email
 * @param {string} credentials.password - Landlord password
 * @returns {Promise<Object>} Login response with tokens and landlord data
 */
export const loginLandlord = async (credentials: Record<string, unknown>) => {
  return fetchWithAuth('/landlords/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
};

/**
 * Get current landlord profile
 * @returns {Promise<Object>} Landlord profile data
 */
export const getLandlordProfile = async () => {
  const landlordId = getStorageItem('landlordId');
  if (!landlordId) {
    throw new Error('No landlord ID found');
  }
  return fetchWithAuth(`/landlords/${landlordId}`);
};

export const getDashboardOverview = async () => {
  return fetchWithAuth('/landlords/dashboard-overview');
};

/**
 * Update landlord profile
 * @param {string} landlordId - Landlord ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated landlord data
 */
export const updateLandlord = async (landlordId: string, updateData: Record<string, unknown>) => {
  return fetchWithAuth(`/landlords/${landlordId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
};

/**
 * Update theme preference
 * @param {string} landlordId - Landlord ID
 * @param {string} themePreference - Theme preference: 'light', 'dark', or 'system'
 * @returns {Promise<Object>} Updated landlord data
 */
export const updateThemePreference = async (landlordId: string, themePreference: string) => {
  return updateLandlord(landlordId, {
    ui_preferences: {
      theme: themePreference
    }
  });
};

/**
 * Delete landlord account
 * @param {string} landlordId - Landlord ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteLandlord = async (landlordId: string) => {
  return fetchWithAuth(`/landlords/${landlordId}`, {
    method: 'DELETE',
  });
};

/**
 * Passwordless authentication functions
 */

/**
 * Initiate passwordless login by sending verification code to email
 * @param {string} email - Landlord email address
 * @returns {Promise<{data: Object|null, error: Object|null}>} Response with session data or error
 */
export const initiatePasswordlessLogin = async (email: string) => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/landlords/passwordless/login/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email }),
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        const message = errorData.detail?.message || errorData.detail || 'Failed to send verification code. Please try again.';
        return {
          data: null,
          error: { message }
        };
      } catch (e) {
        return {
          data: null,
          error: {
            message: 'Failed to send verification code. Please try again.'
          }
        };
      }
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    const e = error as Error;
    return {
      data: null,
      error: {
        message: e.message || 'Failed to send verification code. Please try again.'
      }
    };
  }
};

/**
 * Verify passwordless login code
 * @param {string} email - Landlord email address
 * @param {string} code - Verification code sent to email
 * @param {string} session - Session token from initiate response
 * @returns {Promise<{data: Object|null, error: Object|null}>} Response with tokens and landlord data or error
 */
export const verifyPasswordlessCode = async (email: string, code: string, session: string) => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/landlords/passwordless/login/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        code,
        session
      }),
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        const message = errorData.detail?.message || errorData.detail || 'Invalid verification code. Please try again.';
        return {
          data: null,
          error: { message }
        };
      } catch (e) {
        return {
          data: null,
          error: {
            message: 'Invalid verification code. Please try again.'
          }
        };
      }
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    const e = error as Error;
    return {
      data: null,
      error: {
        message: e.message || 'Failed to verify code. Please try again.'
      }
    };
  }
};

/**
 * Passwordless signup - register a new landlord via passwordless signup
 * @param {Object} signupData - Signup data
 * @param {string} signupData.email - Landlord email address
 * @param {string} signupData.first_name - First name
 * @param {string} signupData.last_name - Last name
 * @param {string} [signupData.password] - Password (optional)
 * @param {string} [signupData.user_status] - User status (defaults to 'waitlist')
 * @returns {Promise<{data: Object|null, error: Object|null}>} Response with signup data or error
 */
export const passwordlessSignup = async (signupData: Record<string, unknown>) => {
  try {
    const response = await fetch(`${getApiBaseUrl()}/landlords/passwordless/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(signupData),
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        const message = errorData.detail?.message || errorData.detail || 'Failed to create account. Please try again.';
        return {
          data: null,
          error: { message }
        };
      } catch (e) {
        return {
          data: null,
          error: {
            message: 'Failed to create account. Please try again.'
          }
        };
      }
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    const e = error as Error;
    return {
      data: null,
      error: {
        message: e.message || 'Failed to create account. Please try again.'
      }
    };
  }
};

/**
 * Confirm signup with verification code
 * @param {string} username - Cognito username
 * @param {string} confirmationCode - Verification code sent to email
 * @param {string} [landlordId] - Landlord ID from signup response
 * @param {Object} [registrationData] - Registration data from signup response
 * @returns {Promise<{data: Object|null, error: Object|null}>} Response with confirmation data or error
 */
export const confirmSignUp = async (username: string, confirmationCode: string, landlordId: string | null = null, registrationData: Record<string, unknown> | null = null) => {
  try {
    const body: {
      username: string;
      confirmation_code: string;
      landlord_id?: string;
      registration_data?: Record<string, unknown>;
    } = {
      username,
      confirmation_code: confirmationCode
    };

    // Add landlord_id and registration_data if provided (required for DDB record creation)
    if (landlordId) {
      body.landlord_id = landlordId;
    }
    if (registrationData) {
      body.registration_data = registrationData;
    }

    const response = await fetch(`${getApiBaseUrl()}/landlords/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
      mode: 'cors',
    });

    if (!response.ok) {
      const errorText = await response.text();
      try {
        const errorData = JSON.parse(errorText);
        const message = errorData.detail?.message || errorData.detail || 'Failed to confirm registration. Please try again.';
        return {
          data: null,
          error: { message }
        };
      } catch (e) {
        return {
          data: null,
          error: {
            message: 'Failed to confirm registration. Please try again.'
          }
        };
      }
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    const e = error as Error;
    return {
      data: null,
      error: {
        message: e.message || 'Failed to confirm registration. Please try again.'
      }
    };
  }
};
